(function (root) {
  'use strict';
  const safeImage = (value) => {
    try {
      const url = new URL(value, 'https://cdn.shopify.com');
      return url.protocol === 'https:' && (url.hostname === 'cdn.shopify.com' || url.hostname.endsWith('.shopify.com')) ? url.href : '';
    } catch (_) { return ''; }
  };
  async function fetchProducts(endpoint, fetcher) {
    const products = new Map();
    for (let page = 1; page <= 20; page += 1) {
      const url = new URL(endpoint);
      url.searchParams.set('limit', '250');
      url.searchParams.set('page', String(page));
      const response = await fetcher(url.toString(), { credentials: 'same-origin', headers: { Accept: 'application/json' }, cache: 'no-store' });
      if (!response.ok) throw new Error('Published Clothing feed unavailable');
      const payload = await response.json();
      if (!Array.isArray(payload.products)) throw new Error('Invalid Clothing feed');
      let added = 0;
      for (const product of payload.products) {
        if (!product || !product.handle || !product.id) throw new Error('Invalid product identity');
        if (!products.has(product.handle)) added += 1;
        products.set(product.handle, product);
      }
      if (payload.products.length < 250) return products;
      if (!added) throw new Error('Clothing pagination repeated');
    }
    throw new Error('Clothing pagination limit reached');
  }
  const parseTags = (live) => (Array.isArray(live.tags) ? live.tags : String(live.tags || '').split(','))
    .map(tag => String(tag).trim().toLowerCase()).filter(Boolean);
  const productId = (value) => String(value || '').split('/').pop();
  const isExcluded = (excluded, id) => {
    if (!excluded || !id) return false;
    const numeric = productId(id);
    for (const blocked of excluded) if (String(blocked) === String(id) || productId(blocked) === numeric) return true;
    return false;
  };
  const hasImportOrigin = (tags) => tags.some(tag => tag === 'bangladesh' || tag === 'china' || tag.includes('bangladesh') || tag.includes('china'));
  const hasUnsupportedImportOrigin = (live, tags) => hasImportOrigin(tags) || /\b(bangladesh|china)\b/i.test(String(live.vendor || ''));
  const INTEREST_ONLY_TAGS = new Set(['coming-soon', 'coming-soon-interest', 'interest-only', 'interest-registration', 'register-interest', 'preorder-interest']);
  const hasInterestOnlyTag = (tags) => tags.some(tag => INTEREST_ONLY_TAGS.has(String(tag).trim().toLowerCase().replace(/[\s_]+/g, '-')));
  const isNonClothing = (live, tags) => {
    const type = String(live.product_type || '').toLowerCase();
    const blockedTerms = ['bag', 'handbag', 'accessor', 'footwear', 'shoe', 'scarf', 'jewelry', 'jewellery'];
    return blockedTerms.some(term => type.includes(term) || tags.some(tag => tag.includes(term)));
  };
  const FAMILY_MAP = {
    'jsk-clothing-outerwear': { Women: 'Outerwear', Men: 'Outerwear' },
    'jsk-clothing-skirts': { Women: 'Skirts' },
    'jsk-clothing-dresses': { Women: 'Dresses' },
    'jsk-clothing-shirts': { Women: 'Blouses & Tops', Men: 'Shirts' },
    'jsk-clothing-tops': { Women: 'Blouses & Tops', Men: 'T-Shirts & Polos' },
    'jsk-clothing-kids-tops': { Kids: 'Tops & Sweatshirts' },
    'jsk-clothing-blouses-tops': { Women: 'Blouses & Tops' },
    'jsk-clothing-sweaters': { Women: 'Sweaters', Men: 'Sweaters' },
    'jsk-clothing-t-shirts-polos': { Men: 'T-Shirts & Polos' },
    'jsk-clothing-trousers': { Men: 'Trousers' }
  };
  function classifyNewProduct(live, excluded) {
    const tags = parseTags(live);
    const id = 'gid://shopify/Product/' + productId(live.id);
    if (!live.handle || !live.id || !live.title || isExcluded(excluded, id)) return null;
    // New rows require both exact sellability tags. Frozen rows retain their prior policy path.
    if (!tags.includes('jsk sellable') || !tags.includes('jsk verified sellable')) return null;
    if (hasUnsupportedImportOrigin(live, tags) || hasInterestOnlyTag(tags) || isNonClothing(live, tags)) return null;
    const audienceTags = ['women', 'men', 'kids', 'unisex'].filter(value => tags.includes('jsk-audience-' + value));
    if (audienceTags.length !== 1) return null;
    let audiences;
    let audience;
    if (audienceTags[0] === 'unisex') {
      audiences = ['Women', 'Men'];
      audience = 'Unisex';
    } else {
      audiences = [{ women: 'Women', men: 'Men', kids: 'Kids' }[audienceTags[0]]];
      audience = audiences[0];
    }
    const familyTags = Object.keys(FAMILY_MAP).filter(value => tags.includes(value));
    if (familyTags.length !== 1) return null;
    const familyTag = familyTags[0];
    // The hierarchy has no unisex leaf for these family tags.
    const categories = audiences.map(value => FAMILY_MAP[familyTag][value]);
    if (categories.some(value => !value) || new Set(categories).size !== 1) return null;
    const subcategory = categories[0];
    const image = (live.images || []).find(entry => entry && safeImage(entry.src));
    if (!image) return null;
    return {
      id, code: '', title: live.title, handle: live.handle, audience, audiences,
      subcategory, policyDisposition: 'IN_SCOPE', status: 'ACTIVE',
      canonicalUrl: '/products/' + encodeURIComponent(live.handle),
      featuredUrl: safeImage(image.src), featuredAsset: '', featuredAlt: image.alt || live.title,
      _tagClassified: true
    };
  }
  function reconcile(rows, published, excluded) {
    const safeRows = Array.isArray(rows) ? rows : [];
    const frozenHandles = new Set(safeRows.map(item => item && item.handle).filter(Boolean));
    const frozenIds = new Set(safeRows.map(item => item && productId(item.id)).filter(Boolean));
    const seenHandles = new Set();
    const seenIds = new Set();
    const result = [];
    const liveProducts = published instanceof Map ? published : new Map();
    const append = (item, live) => {
      const numericId = productId(item.id);
      if (!item.handle || !numericId || seenHandles.has(item.handle) || seenIds.has(numericId)) return;
      if (isExcluded(excluded, item.id)) return;
      const tags = parseTags(live);
      if (tags.includes('bags') || tags.includes('accessories') || ['bags', 'handbags', "women's handbags", 'accessories', 'footwear', 'scarves', 'jewelry'].includes(String(live.product_type || '').toLowerCase())) return;
      const image = (live.images || []).find(entry => entry && safeImage(entry.src));
      if (!image) return;
      const unisex = tags.includes('jsk-audience-unisex');
      const taggedAudiences = ['Women', 'Men', 'Kids'].filter(value => tags.includes('jsk-audience-' + value.toLowerCase()) || (unisex && value !== 'Kids'));
      const audiences = taggedAudiences.length ? taggedAudiences : (item.audience === 'Unisex' ? ['Women', 'Men'] : [item.audience].filter(Boolean));
      const audience = audiences.length > 1 ? (unisex && audiences.length === 2 ? 'Unisex' : audiences.join(' & ')) : audiences[0];
      const familyTags = Object.keys(FAMILY_MAP).filter(tag => tags.includes(tag));
      const familyTag = familyTags.length === 1 ? familyTags[0] : '';
      const familyCategories = familyTag ? audiences.map(value => FAMILY_MAP[familyTag][value]) : [];
      const familyMatchesAudience = familyCategories.length && familyCategories.every(Boolean);
      const subcategory = familyMatchesAudience ? (new Set(familyCategories).size === 1 ? familyCategories[0] : (item.subcategory || '')) : '';
      const subcategoriesByAudience = familyMatchesAudience && new Set(familyCategories).size > 1 ? Object.fromEntries(audiences.map((value, index) => [value, familyCategories[index]])) : undefined;
      item = { ...item, audience: audience || item.audience, audiences, subcategory: subcategory || item.subcategory, subcategoriesByAudience };
      const variants = Array.isArray(live.variants) ? live.variants : [];
      const availablePrices = variants.filter(variant => variant && variant.available === true).map(variant => Number(variant.price)).filter(price => Number.isFinite(price) && price > 0);
      const allPrices = variants.map(variant => Number(variant && variant.price)).filter(price => Number.isFinite(price) && price > 0);
      const importInterest = hasImportOrigin(tags);
      // Preserve existing price behavior when every option is unavailable. If stock exists,
      // never let a cheaper sold-out option understate the purchasable price range.
      const priceBasis = availablePrices.length ? availablePrices : allPrices;
      const publicPrice = !importInterest && priceBasis.length ? Math.min(...priceBasis) : null;
      const publicMaxPrice = !importInterest && availablePrices.length ? Math.max(...availablePrices) : null;
      const currentAvailable = availablePrices.length > 0;
      const interestOnly = importInterest && !currentAvailable;
      const purchaseReady = currentAvailable && tags.includes('jsk sellable') && tags.includes('jsk verified sellable') && !importInterest && !hasInterestOnlyTag(tags);
      const output = { ...item, status: 'ACTIVE', title: live.title || item.title, canonicalUrl: '/products/' + encodeURIComponent(live.handle), featuredUrl: safeImage(image.src), featuredAsset: '', featuredAlt: image.alt || live.title || item.title, currentAvailable, interestOnly, importInterest, publicPrice, publicMaxPrice, purchaseReady };
      delete output._tagClassified;
      result.push(output);
      seenHandles.add(item.handle);
      seenIds.add(numericId);
    };
    // Frozen classifications have precedence and keep their exact disposition/exclusion policy.
    for (const original of safeRows) {
      if (!original || original.policyDisposition !== 'IN_SCOPE') continue;
      const live = liveProducts.get(original.handle);
      if (!live || String(live.id) !== productId(original.id) || isExcluded(excluded, original.id)) continue;
      append(original, live);
    }
    // Expand only products absent from the frozen list and supported by live exact tags.
    for (const live of liveProducts.values()) {
      if (!live || frozenHandles.has(live.handle) || frozenIds.has(productId(live.id))) continue;
      const item = classifyNewProduct(live, excluded);
      if (item) append(item, live);
    }
    return result;
  }
  const api = { fetchProducts, reconcile, classifyNewProduct };
  if (typeof module !== 'undefined' && module.exports) module.exports = api;
  else root.JSKClothingLiveData = api;
})(typeof window !== 'undefined' ? window : globalThis);


